/**
 * What an asset reference is allowed to be.
 *
 * Every `audioRef` and `imageRef` in the course is a path that ends up joined to
 * a directory on a server and handed to a file read. That makes this the most
 * security-sensitive string in the content
 * ([Security](../../docs/SECURITY.md)): a ref like `../../etc/passwd` is a file
 * disclosure, and one like `audio/x.svg` is a content type the browser treats
 * differently from what the folder promised.
 *
 * So the rule lives here — pure, in `core`, with no filesystem in sight — and
 * both the linter and the server use *this* function rather than each writing
 * their own approximation. The linter's job is then to add the one thing this
 * cannot know: whether the file is actually there.
 *
 * It is an **allow-list**. A deny-list of dangerous shapes is a list somebody
 * has to keep complete; an allow-list of two folders, a character set and a few
 * extensions fails closed on everything nobody thought of — `..`, a leading
 * slash, a backslash, a percent-encoded separator, a control character, a
 * colon, a query string. None of them are enumerated below, and none of them
 * get through.
 */
/**
 * Where an asset lives, what may live there, and what it is served as.
 *
 * One table rather than two. An extension list and a separate content-type map
 * are two things that must agree, and the day they stop agreeing is the day an
 * `.svg` is accepted by one and served as `application/octet-stream` by the
 * other. Here the type *is* the membership test, so they cannot drift.
 */
export const ASSET_FOLDERS = {
    /** Recorded speech: the words, and Sneakers' narration. */
    audio: {
        '.m4a': 'audio/mp4',
        '.mp3': 'audio/mpeg',
        '.ogg': 'audio/ogg',
        '.wav': 'audio/wav',
    },
    /** Word pictures, scenes and item art. */
    images: {
        '.svg': 'image/svg+xml',
        '.png': 'image/png',
        '.webp': 'image/webp',
        '.jpg': 'image/jpeg',
    },
    /**
     * Lip-sync tracks, generated alongside each narration clip
     * ([D-018](../../docs/DECISIONS.md#d-018)). Data rather than media, but it is
     * fetched by the same client from the same root, so it is bound by the same
     * rule rather than by a second one somebody has to remember.
     */
    visemes: { '.json': 'application/json' },
};
export const ASSET_FOLDER_NAMES = Object.keys(ASSET_FOLDERS);
/**
 * One path segment: unreserved ASCII, and it must start with a letter or digit.
 *
 * This one expression is what makes the module safe, so it is worth reading as
 * a list of what it excludes rather than what it allows. No `/` inside a
 * segment, so nothing collapses. No `\`, which Windows would treat as a
 * separator. No `%`, so `%2e%2e%2f` cannot become `../` in whatever decodes
 * next. No `:` or `?` or `#`, so a ref can never become a URL with an authority
 * or a query. Nothing outside printable ASCII, so no control characters and no
 * Unicode that normalises to a separator. And no leading dot, which takes `.`,
 * `..` and dotfiles with it.
 */
const SEGMENT = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;
/** Long enough for any real path, short enough to bound the work. */
export const MAX_ASSET_REF = 200;
export function readAssetRef(ref) {
    if (ref.length === 0 || ref.length > MAX_ASSET_REF)
        return 'shape';
    const segments = ref.split('/');
    // A bare filename has no folder to check, so it is refused before anything
    // tries to guess one.
    if (segments.length < 2)
        return 'shape';
    if (!segments.every((segment) => SEGMENT.test(segment)))
        return 'shape';
    const [folder] = segments;
    if (folder === undefined || !Object.hasOwn(ASSET_FOLDERS, folder))
        return 'folder';
    // Taken from the whole reference rather than from its last segment, and only
    // counted when the dot comes after the last slash — so `audio/a.b/c` has no
    // extension rather than borrowing one from a directory name.
    const dot = ref.lastIndexOf('.');
    const extension = dot > ref.lastIndexOf('/') ? ref.slice(dot).toLowerCase() : '';
    const types = ASSET_FOLDERS[folder];
    const contentType = Object.hasOwn(types, extension) ? types[extension] : undefined;
    if (contentType === undefined)
        return 'extension';
    return { folder: folder, path: ref, contentType };
}
/** Whether a refusal came back rather than a reference. */
export const isRefusal = (result) => typeof result === 'string';
/** What to send a browser for a reference this module has already accepted. */
export const assetContentType = (ref) => ref.contentType;
//# sourceMappingURL=assets.js.map